import React, { useEffect, useState } from "react";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import PageLayout from "../../layouts/PageLayout";
import GalleryUpload from "../ProductCreator/UploadImages";

export default function ProductCreatorCreateProductPage() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // New state to track if product is saved and get its ID
    const [createdProductId, setCreatedProductId] = useState(null);

    // Dropdown options states matching initialData() response keys
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [stores, setStores] = useState([]);
    const [caseMaterials, setCaseMaterials] = useState([]);
    const [brandMaterials, setBrandMaterials] = useState([]);
    const [displayTypes, setDisplayTypes] = useState([]);
    const [movementTypes, setMovementTypes] = useState([]);

    // Dependent category states
    const [selectedParentCategory, setSelectedParentCategory] = useState("");
    const [availableSubCategories, setAvailableSubCategories] = useState([]);

    // Form Data state matching backend validation rules & new attributes
    const [formData, setFormData] = useState({
        store_id: "",
        brand_id: "",
        category_id: "", // sub_category_id
        title: "",
        product_condition: "new",
        selling_style: "Fixed",
        product_qty: 1,
        short_buying_price: "",
        buying_price: "",
        sell_price_dollar: "",
        shipping_charges_dollar: "",
        short_description: "",
        description: "",
        case_material: "",
        brand_material: "",
        display_type: "",
        movement_type: "",
    });

    const [mainImage, setMainImage] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState(null);

    const authHeaders = {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    };

    // Fetch initial data (brands, stores, product_category, and specifications)
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/product-creator/initial-data`, {
                    headers: authHeaders,
                });

                const resData = response.data.data || response.data;
                setCategories(resData.product_category || []);
                setBrands(resData.brand || []);
                setStores(resData.store || []);
                setCaseMaterials(resData.case_material || []);
                setBrandMaterials(resData.brand_material || []);
                setDisplayTypes(resData.display_type || []);
                setMovementTypes(resData.movement_type || []);
            } catch (err) {
                console.error("Error fetching initial form data:", err);
                setError("Failed to load initial form options.");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    const handleParentCategoryChange = (e) => {
        const parentId = e.target.value;
        setSelectedParentCategory(parentId);

        const selectedCat = categories.find((cat) => String(cat.id) === String(parentId));
        const children = selectedCat ? selectedCat.child_categories || [] : [];

        setAvailableSubCategories(children);
        setFormData((prev) => ({ ...prev, category_id: "" }));
    };

    const handleSubCategoryChange = (e) => {
        const subCatId = e.target.value;
        setFormData((prev) => ({ ...prev, category_id: subCatId }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleShortDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, short_description: content }));
    };

    const handleDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, description: content }));
    };

    const handleMainImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setMainImage(file);
        setMainImagePreview(URL.createObjectURL(file));
    };

    const validateForm = () => {
        if (!formData.store_id) {
            setError("Store is required.");
            return false;
        }
        if (!formData.brand_id) {
            setError("Brand is required.");
            return false;
        }
        if (!formData.category_id) {
            setError("Sub-category is required.");
            return false;
        }
        if (!formData.title.trim()) {
            setError("Product title is required.");
            return false;
        }
        if (!formData.buying_price || Number(formData.buying_price) < 0) {
            setError("Buying price is required and must be valid.");
            return false;
        }
        if (!formData.sell_price_dollar || Number(formData.sell_price_dollar) <= 0) {
            setError("Selling Price ($) is required and must be greater than 0.");
            return false;
        }
        if (formData.shipping_charges_dollar === "" || Number(formData.shipping_charges_dollar) < 0) {
            setError("Shipping charges are required.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!validateForm()) return;

        setSaving(true);

        try {
            const submitData = new FormData();
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
                    submitData.append(key, formData[key]);
                }
            });

            if (mainImage) {
                submitData.append("card_img", mainImage);
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/product-creator/products`,
                submitData,
                {
                    headers: {
                        ...authHeaders,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const resData = response.data;
            if (!resData.success) {
                setError(resData.message || "Failed to create product.");
                return;
            }

            setSuccess("Product created successfully! Now you can upload gallery images.");
            const newId = resData.data?.id;

            if (newId) {
                setCreatedProductId(newId);
            } else {
                navigate(`/product-creator/products`);
            }
        } catch (err) {
            console.error("Creation error:", err);
            if (err.response?.data?.errors) {
                const messages = Object.values(err.response.data.errors).flat().join(" ");
                setError(messages);
            } else {
                setError(err.response?.data?.message || "Failed to create product.");
            }
        } finally {
            setSaving(false);
        }
    };

    const editorButtonList = [
        ['undo', 'redo'],
        ['font', 'fontSize', 'formatBlock'],
        ['bold', 'underline', 'italic', 'strike'],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list'],
        ['table', 'link', 'image'],
        ['fullScreen', 'codeView']
    ];

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-3 text-muted">Loading creation form...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">
                                {createdProductId ? "Upload Product Gallery Images" : "Create Product"}
                            </h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Home</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Products</li>
                                <li className="mc-breadcrumb-item">
                                    {createdProductId ? "Gallery" : "Create Product"}
                                </li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}
                </Col>

                <Col xl={12}>
                    {!createdProductId ? (
                        <Form onSubmit={handleSubmit}>
                            {/* RELATIONS & STORES */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header><h5 className="mb-0">Store & Brand Assignment</h5></Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Store <span className="text-danger">*</span></Form.Label>
                                                <Form.Select name="store_id" value={formData.store_id} onChange={handleChange} required>
                                                    <option value="">Select Store</option>
                                                    {stores.map((s) => (
                                                        <option key={s.id} value={s.id}>{s.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Brand <span className="text-danger">*</span></Form.Label>
                                                <Form.Select name="brand_id" value={formData.brand_id} onChange={handleChange} required>
                                                    <option value="">Select Brand</option>
                                                    {brands.map((b) => (
                                                        <option key={b.id} value={b.id}>{b.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* CATEGORIES SELECTION */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header><h5 className="mb-0">Categories Selection</h5></Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Parent Category <span className="text-danger">*</span></Form.Label>
                                                <Form.Select value={selectedParentCategory} onChange={handleParentCategoryChange}>
                                                    <option value="">Select Parent Category</option>
                                                    {categories.map((cat) => (
                                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Sub Category <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    name="category_id"
                                                    value={formData.category_id}
                                                    onChange={handleSubCategoryChange}
                                                    disabled={!selectedParentCategory}
                                                    required
                                                >
                                                    <option value="">Select Sub Category</option>
                                                    {availableSubCategories.map((sub) => (
                                                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* PRODUCT SPECIFICATIONS (Case Material, Band Material, Display Type, Movement Type) */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header><h5 className="mb-0">Product Specifications</h5></Card.Header>
                                <Card.Body>
                                    <Row>
                                        {/* Case Material */}
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Case Material</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="case_material"
                                                    list="caseMaterialsList"
                                                    value={formData.case_material}
                                                    onChange={handleChange}
                                                    placeholder="Select or type..."
                                                />
                                                <datalist id="caseMaterialsList">
                                                    {caseMaterials.map((item, idx) => (
                                                        <option key={idx} value={item} />
                                                    ))}
                                                </datalist>
                                            </Form.Group>
                                        </Col>

                                        {/* Band Material */}
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Band Material</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="brand_material"
                                                    list="brandMaterialsList"
                                                    value={formData.brand_material}
                                                    onChange={handleChange}
                                                    placeholder="Select or type..."
                                                />
                                                <datalist id="brandMaterialsList">
                                                    {brandMaterials.map((item, idx) => (
                                                        <option key={idx} value={item} />
                                                    ))}
                                                </datalist>
                                            </Form.Group>
                                        </Col>

                                        {/* Display Type */}
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Display Type</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="display_type"
                                                    list="displayTypesList"
                                                    value={formData.display_type}
                                                    onChange={handleChange}
                                                    placeholder="Select or type..."
                                                />
                                                <datalist id="displayTypesList">
                                                    {displayTypes.map((item, idx) => (
                                                        <option key={idx} value={item} />
                                                    ))}
                                                </datalist>
                                            </Form.Group>
                                        </Col>

                                        {/* Movement Type */}
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Movement Type</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="movement_type"
                                                    list="movementTypesList"
                                                    value={formData.movement_type}
                                                    onChange={handleChange}
                                                    placeholder="Select or type..."
                                                />
                                                <datalist id="movementTypesList">
                                                    {movementTypes.map((idx, item) => (
                                                        <option key={idx} value={item} />
                                                    ))}
                                                </datalist>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* PRODUCT INFORMATION & PRICING */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header><h5 className="mb-0">Product Information & Pricing</h5></Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Product Title <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="text" name="title" value={formData.title} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Condition</Form.Label>
                                                <Form.Select name="product_condition" value={formData.product_condition} onChange={handleChange}>
                                                    <option value="new">New</option>
                                                    <option value="used">Used</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Selling Style</Form.Label>
                                                <Form.Select name="selling_style" value={formData.selling_style} onChange={handleChange}>
                                                    <option value="Fixed">Fixed</option>
                                                    <option value="Auction">Auction</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Quantity</Form.Label>
                                                <Form.Control type="number" name="product_qty" value={formData.product_qty} onChange={handleChange} min="0" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Short Buying Price</Form.Label>
                                                <Form.Control type="text" name="short_buying_price" placeholder="e.g. ABC" value={formData.short_buying_price} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Buying Price <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="number" step="0.01" name="buying_price" value={formData.buying_price} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Sell Price ($) <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="number" step="0.01" name="sell_price_dollar" value={formData.sell_price_dollar} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Shipping Charges ($) <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="number" step="0.01" name="shipping_charges_dollar" value={formData.shipping_charges_dollar} onChange={handleChange} required />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* SHORT DETAILS */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header><h5 className="mb-0">Short Details</h5></Card.Header>
                                <Card.Body>
                                    <SunEditor
                                        setContents={formData.short_description}
                                        onChange={handleShortDescriptionChange}
                                        setOptions={{ buttonList: editorButtonList, height: 180 }}
                                    />
                                </Card.Body>
                            </Card>

                            {/* MAIN IMAGE */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header><h5 className="mb-0">Main Image <span className="text-danger">*</span></h5></Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={3}>
                                            {mainImagePreview && (
                                                <img src={mainImagePreview} alt="Preview" style={{ width: "100%", height: "150px", objectFit: "contain" }} />
                                            )}
                                        </Col>
                                        <Col md={9}>
                                            <Form.Control type="file" accept="image/*" onChange={handleMainImageChange} required />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* LONG DESCRIPTION */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header><h5 className="mb-0">Long Description</h5></Card.Header>
                                <Card.Body>
                                    <SunEditor
                                        setContents={formData.description}
                                        onChange={handleDescriptionChange}
                                        setOptions={{ buttonList: editorButtonList, height: 300 }}
                                    />
                                </Card.Body>
                            </Card>

                            {/* SUBMIT BUTTONS */}
                            <Card className="mb-5 shadow-sm">
                                <Card.Body className="d-flex justify-content-end gap-2">
                                    <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
                                    <Button type="submit" variant="primary" disabled={saving}>
                                        {saving ? <Spinner size="sm" animation="border" /> : "Save & Create"}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Form>
                    ) : (
                        <div>
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Product Gallery</h5>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => navigate("/product-creator/products")}
                                    >
                                        Done / Finish
                                    </Button>
                                </Card.Header>
                                <Card.Body>
                                    <GalleryUpload pId={createdProductId} images={[]} />
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Col>
            </Row>
        </PageLayout>
    );
}